import React, { useState } from 'react';
import BaseModal, {
	BaseModalProps,
} from '../../../Base/Components/BaseModal/BaseModal';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import DateField from '../../../../Components/Form/components/DateField';
import { useForm } from 'react-hook-form';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import moment from 'moment';
import usePatientMedicalHistory from '../../Hooks/usePatientMedicalHistory';
import { PreviewFile } from '../../../../Components/MediaViewer/MediaViewer';
import MediaViewerModal from '../../../../Components/MediaViewer/MediaViewerModal';
import useFilters from '../../../Base/Hooks/useFilters';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useTranslation from '../../../../Hooks/useTranslation';
import InputWrapper from '../../../../Components/Form/components/InputWrapper/InputWrapper';
import useNotification from '../../../../Hooks/useNotification.hook';
import {
	DocumentationFile,
	useGenerateDocumentPdfWithDateMutation,
} from '../../../../graphql/graphql';
import { prepareDocumentationFileForMediaViewer } from '../../../Ecard/Services/eCardFile.service';
import { useAppContext } from '../../../../Services/AppContext';
import useLoading from '../../../Base/Hooks/useLoading';

interface Props extends BaseModalProps {
	patientId: string;
}

interface FormValues {
	date: string;
}

const validationSchema = yup.object({
	date: yup
		.date()
		.required('validation.patientMedicalHistoryPrintModal.date')
		.typeError('validation.patientMedicalHistoryPrintModal.date'),
});

const PatientMedicalHistoryPrintModal: React.FC<Props> = props => {
	const { isLoading, setIsLoading, RenderLoading } = useLoading();
	const [documentationFile, setDocumentationFile] = useState<
		DocumentationFile | undefined
	>(undefined);
	const { socketUrl } = useAppContext();
	const { medicalHistoryDocumentId } = usePatientMedicalHistory(
		props.patientId,
	);

	const [createDocumentPdfWithDate] = useGenerateDocumentPdfWithDateMutation();

	const formMethods = useForm({
		defaultValues: {
			date: moment(),
		},
		resolver: yupResolver(validationSchema),
	});

	const handleSubmit = async (values: FormValues) => {
		if (!medicalHistoryDocumentId) return;
		setIsLoading(true);
		const res = await createDocumentPdfWithDate({
			input: { documentId: medicalHistoryDocumentId, printDate: values.date },
		}).unwrap();

		const file = res?.generateDocumentPDFWithDate;
		if (file) {
			setDocumentationFile(file as DocumentationFile);
		}
		setIsLoading(false);
	};

	if (documentationFile) {
		return (
			<MediaViewerModal
				opened
				onClose={props.onClose}
				file={prepareDocumentationFileForMediaViewer(
					documentationFile,
					socketUrl,
				)}
				isSignable={documentationFile.canBeSigned || false}
			/>
		);
	}

	let modalContent = (
		<FormWrapper formMethods={formMethods}>
			<InputWrapper>
				<DateField label='base.shared.date' name='date' isRequired />
			</InputWrapper>
			<SubmitButton
				onSubmit={handleSubmit}
				title='component.patientMedicalHistory.print'
			/>
		</FormWrapper>
	);

	if (isLoading) {
		modalContent = <RenderLoading />;
	}

	return (
		<BaseModal title='component.patientMedicalHistory.label' {...props}>
			{modalContent}
		</BaseModal>
	);
};

export default PatientMedicalHistoryPrintModal;
